import React, { createContext, useContext, useState, useRef, useEffect, useCallback, ReactNode } from 'react';
import { assetUrl } from '../utils/assets';
import { Howl } from 'howler';
import { fireAnalyticsEvent } from '../utils/analytics';
import { MUSIC_ENABLED_KEY } from '../utils/audioConsent';

interface MusicState {
  enabled: boolean;
  volume: number;
  isPlaying: boolean;
}

interface BackgroundMusicContextValue {
  state: MusicState;
  toggleEnabled: () => void;
  play: () => void;
  playFromRandom: () => void;
  fadeOut: () => void;
}

const BackgroundMusicContext = createContext<BackgroundMusicContextValue | undefined>(undefined);

const MUSIC_VOLUME_KEY = 'percy_music_volume';

export function BackgroundMusicProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<MusicState>(() => {
    const savedEnabled = localStorage.getItem(MUSIC_ENABLED_KEY);
    const savedVolume = localStorage.getItem(MUSIC_VOLUME_KEY);
    return {
      enabled: savedEnabled === 'true',
      volume: savedVolume !== null ? parseFloat(savedVolume) : 0.3,
      isPlaying: false,
    };
  });

  useEffect(() => {
    const handler = () => {
      const saved = localStorage.getItem(MUSIC_ENABLED_KEY);
      setState(prev => ({ ...prev, enabled: saved === 'true' }));
    };
    window.addEventListener('percy-audio-consent', handler);
    return () => window.removeEventListener('percy-audio-consent', handler);
  }, []);

  const howlRef = useRef<Howl | null>(null);
  const isLoadedRef = useRef(false);

  useEffect(() => {
    const howl = new Howl({
      src: [assetUrl('/sounds/background-music.mp3')],
      html5: true,
      loop: true,
      volume: state.enabled ? state.volume : 0,
      preload: true,
      onload: () => {
        isLoadedRef.current = true;
      },
    });

    howlRef.current = howl;

    return () => {
      howlRef.current?.unload();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (howlRef.current) {
      howlRef.current.volume(state.enabled ? state.volume : 0);
    }
  }, [state.volume, state.enabled]);

  const toggleEnabled = useCallback(() => {
    setState(prev => {
      const newEnabled = !prev.enabled;
      localStorage.setItem(MUSIC_ENABLED_KEY, String(newEnabled));
      if (howlRef.current) {
        howlRef.current.volume(newEnabled ? prev.volume : 0);
      }
      fireAnalyticsEvent('audio_toggled', { channel: 'music', new_state: newEnabled, source: 'corner_toggle' });
      return { ...prev, enabled: newEnabled };
    });
  }, []);

  const play = useCallback(() => {
    if (!howlRef.current) return;
    if (howlRef.current.playing()) return;

    howlRef.current.seek(0);
    howlRef.current.volume(state.enabled ? state.volume : 0);
    howlRef.current.play();
    setState(prev => ({ ...prev, isPlaying: true }));
  }, [state.enabled, state.volume]);

  const playFromRandom = useCallback(() => {
    if (!howlRef.current) return;
    if (howlRef.current.playing()) return;

    const duration = howlRef.current.duration();
    if (duration > 0) {
      const randomPosition = Math.random() * (duration / 2);
      howlRef.current.seek(randomPosition);
    }
    howlRef.current.volume(state.enabled ? state.volume : 0);
    howlRef.current.play();
    setState(prev => ({ ...prev, isPlaying: true }));
  }, [state.enabled, state.volume]);

  const fadeOut = useCallback(() => {
    if (!howlRef.current || !howlRef.current.playing()) return;

    howlRef.current.fade(howlRef.current.volume(), 0, 500);
    setTimeout(() => {
      if (howlRef.current) {
        howlRef.current.stop();
        howlRef.current.volume(state.enabled ? state.volume : 0);
      }
      setState(prev => ({ ...prev, isPlaying: false }));
    }, 500);
  }, [state.enabled, state.volume]);

  const value: BackgroundMusicContextValue = {
    state,
    toggleEnabled,
    play,
    playFromRandom,
    fadeOut,
  };

  return (
    <BackgroundMusicContext.Provider value={value}>
      {children}
    </BackgroundMusicContext.Provider>
  );
}

export function useBackgroundMusic() {
  const context = useContext(BackgroundMusicContext);
  if (context === undefined) {
    throw new Error('useBackgroundMusic must be used within a BackgroundMusicProvider');
  }
  return context;
}
